import { toast } from 'sonner';
import { formatDateTime } from '@/shared/lib/format';

/** Warum eine Support-Sitzung im Tab endet. */
export type SupportEndReason =
  | 'ended'
  | 'expired'
  | 'closed'
  | 'workspace-locked'
  | 'not-allowed'
  | 'home-changed'
  | 'signed-out';

export interface SupportNotice {
  reason: SupportEndReason;
  /** `null`, wenn ein Tab nach dem Neuladen nur noch die Id kannte. */
  workspaceName: string | null;
  homeWorkspaceName?: string | null;
  /** Bis wann die Sitzung beim Server noch laufen kann, weil der Tab ihr Ende nicht bestätigt bekam. */
  openUntil?: string;
}

export function supportNoticeText({
  reason,
  workspaceName,
  homeWorkspaceName,
  openUntil,
}: SupportNotice): string | null {
  const target = workspaceName ? `„${workspaceName}“` : null;
  const back = homeWorkspaceName
    ? `Sie arbeiten wieder in ${homeWorkspaceName}.`
    : 'Sie arbeiten wieder in Ihrem eigenen Workspace.';
  const until = openUntil ? formatDateTime(openUntil) : null;
  switch (reason) {
    case 'ended':
      return `Support-Sitzung in ${target ?? 'diesem Workspace'} beendet. ${back}`;
    case 'expired':
      return `Die Support-Sitzung in ${target ?? 'diesem Workspace'} ist abgelaufen. ${back}`;
    case 'closed':
      return target
        ? `Die Support-Sitzung in ${target} wurde beendet – in einem anderen Tab oder durch eine neue Sitzung. ${back}`
        : `Die Support-Sitzung dieses Tabs läuft nicht mehr. ${back}`;
    case 'workspace-locked':
      return `${target ?? 'Der Workspace'} wurde gesperrt, die Support-Sitzung ist beendet. ${back}`;
    case 'not-allowed':
      return `Ihr Konto darf keine Support-Sitzung mehr führen, etwa weil die Zwei-Faktor-Anmeldung fehlt. ${back}`;
    case 'home-changed':
      return `In einem anderen Tab hat sich ein anderes Konto angemeldet. Die Support-Sitzung in ${target ?? 'diesem Tab'} ist hier beendet${until ? `; beim Server läuft sie bis spätestens ${until}` : ''}.`;
    case 'signed-out':
      return until
        ? `Die Support-Sitzung in ${target ?? 'diesem Workspace'} ließ sich beim Abmelden nicht beenden. Sie läuft beim Server bis spätestens ${until} weiter.`
        : null;
  }
}

/** Der Wechsel baut die Arbeitsfläche neu auf; die Meldung steht außerhalb davon und bleibt lesbar. */
export function showSupportNotice(notice: SupportNotice): void {
  const text = supportNoticeText(notice);
  if (!text) return;
  if (notice.reason === 'ended') toast.success(text);
  else if (notice.reason === 'signed-out') toast.error(text, { duration: 15_000 });
  else toast.info(text, { duration: 10_000 });
}
